/**
 * Frontend logging utility with dual logging strategy:
 * 
 * - Console Logs: Basic, human-readable messages for developers
 * - App Logs: Structured, detailed data for analytics/monitoring
 * 
 * Provides consistent logging that respects NODE_ENV and NEXT_PUBLIC_ENVIRONMENT:
 * - Local development: Full logging to console + structured app logs
 * - Azure dev/staging: Full logging to console + structured app logs
 * - Azure production: Only error/warn to console, all logs to app logs (for monitoring)
 */

/**
 * Check if verbose logging should be enabled for console
 * Uses runtime hostname detection as fallback when NEXT_PUBLIC_ENVIRONMENT isn't available at build time
 */
function shouldEnableVerboseConsoleLogging(): boolean {
  // Always enable in local development
  if (process.env.NODE_ENV === 'development') {
    return true;
  }
  
  // Check NEXT_PUBLIC_ENVIRONMENT for Azure environments (build-time)
  const environment = process.env.NEXT_PUBLIC_ENVIRONMENT?.toLowerCase();
  if (environment === 'dev' || environment === 'staging') {
    return true;
  }
  
  // Fallback: Check hostname at runtime (for cases where NEXT_PUBLIC_ENVIRONMENT isn't set at build time)
  // This works because hostname is available at runtime in the browser
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname.toLowerCase();
    // Check if hostname contains '.dev.' (e.g., dt-debtcast.dev.devtechlab.com)
    if (hostname.includes('.dev.')) {
      return true;
    }
  }
  
  return false;
}

/**
 * Structured log entry for app logs (detailed, structured data)
 */
interface AppLogEntry {
  level: 'debug' | 'info' | 'warn' | 'error';
  message: string;
  timestamp: string;
  context?: Record<string, unknown>;
  error?: {
    message: string;
    stack?: string;
  };
  userAgent?: string;
  url?: string;
}

/**
 * Store app logs in memory (could be sent to external service)
 * In production, this could be sent to a logging service (e.g., LogRocket, Datadog, etc.)
 */
const appLogs: AppLogEntry[] = [];
const MAX_APP_LOGS = 100; // Keep last 100 logs in memory

function addAppLog(entry: AppLogEntry): void {
  appLogs.push(entry);
  // Keep only the last MAX_APP_LOGS entries
  if (appLogs.length > MAX_APP_LOGS) {
    appLogs.shift();
  }
  
  // In production, could send to external logging service
  // TODO: Integrate with logging service (e.g., LogRocket, Datadog, etc.)
  // if (process.env.NODE_ENV === 'production') {
  //   sendToLoggingService(entry);
  // }
}

/**
 * Format console message (basic, human-readable)
 */
function formatConsoleMessage(message: string, context?: Record<string, unknown> | unknown): string {
  if (!context) {
    return message;
  }
  
  // Handle non-object contexts (strings, arrays, etc.)
  if (typeof context !== 'object' || context === null || Array.isArray(context)) {
    return `${message} (${String(context)})`;
  }
  
  // Handle Record<string, unknown>
  const contextObj = context as Record<string, unknown>;
  if (Object.keys(contextObj).length === 0) {
    return message;
  }
  
  // For console, show a simple summary
  const contextSummary = Object.entries(contextObj)
    .slice(0, 3) // Only show first 3 context items in console
    .map(([key, value]) => {
      // Truncate long values for console readability
      const strValue = typeof value === 'string' && value.length > 50 
        ? `${value.substring(0, 50)}...` 
        : String(value);
      return `${key}: ${strValue}`;
    })
    .join(', ');
  
  return contextSummary ? `${message} (${contextSummary})` : message;
}

/**
 * Normalize context to Record<string, unknown>
 */
function normalizeContext(context?: Record<string, unknown> | unknown): Record<string, unknown> | undefined {
  if (!context) {
    return undefined;
  }
  
  // Handle non-object contexts
  if (typeof context !== 'object' || context === null) {
    return { value: String(context) };
  }
  
  // Handle arrays
  if (Array.isArray(context)) {
    return { array: context };
  }
  
  // Handle Error objects
  if (context instanceof Error) {
    return {
      error: context.message,
      stack: context.stack,
    };
  }
  
  // Handle Record<string, unknown>
  return context as Record<string, unknown>;
}

export interface Logger {
  debug: (message: string, context?: Record<string, unknown> | unknown) => void;
  info: (message: string, context?: Record<string, unknown> | unknown) => void;
  warn: (message: string, context?: Record<string, unknown> | unknown) => void;
  error: (message: string, context?: Record<string, unknown> | unknown) => void;
}

/**
 * Frontend logger with dual logging strategy:
 * 
 * Console Logs (basic, human-readable):
 * - debug: Only in development/dev/staging (verbose logging environments)
 * - info: Only in development/dev/staging (verbose logging environments)
 * - warn: Always logs (all environments)
 * - error: Always logs (all environments)
 * 
 * App Logs (structured, detailed):
 * - All logs are stored with full context (for analytics/monitoring)
 * - Can be sent to external logging service in production
 */
export const logger: Logger = {
  debug: (message: string, context?: Record<string, unknown> | unknown) => {
    const normalizedContext = normalizeContext(context);
    const entry: AppLogEntry = {
      level: 'debug',
      message,
      timestamp: new Date().toISOString(),
      context: normalizedContext,
      ...(typeof window !== 'undefined' && window.location && {
        userAgent: navigator.userAgent,
        url: window.location.href,
      }),
    };
    
    // Always add to app logs (structured, detailed)
    addAppLog(entry);
    
    // Console: Only in verbose environments
    if (shouldEnableVerboseConsoleLogging()) {
      console.debug(formatConsoleMessage(message, context), normalizedContext || '');
    }
  },

  info: (message: string, context?: Record<string, unknown> | unknown) => {
    const normalizedContext = normalizeContext(context);
    const entry: AppLogEntry = {
      level: 'info',
      message,
      timestamp: new Date().toISOString(),
      context: normalizedContext,
      ...(typeof window !== 'undefined' && window.location && {
        userAgent: navigator.userAgent,
        url: window.location.href,
      }),
    };
    
    // Always add to app logs (structured, detailed)
    addAppLog(entry);
    
    // Console: Only in verbose environments, basic message
    if (shouldEnableVerboseConsoleLogging()) {
      console.info(formatConsoleMessage(message, context));
    }
  },

  warn: (message: string, context?: Record<string, unknown> | unknown) => {
    const normalizedContext = normalizeContext(context);
    const entry: AppLogEntry = {
      level: 'warn',
      message,
      timestamp: new Date().toISOString(),
      context: normalizedContext,
      ...(typeof window !== 'undefined' && window.location && {
        userAgent: navigator.userAgent,
        url: window.location.href,
      }),
    };
    
    // Always add to app logs (structured, detailed)
    addAppLog(entry);
    
    // Console: Always log warnings (basic message)
    console.warn(formatConsoleMessage(message, context), normalizedContext || '');
  },

  error: (message: string, context?: Record<string, unknown> | unknown) => {
    const normalizedContext = normalizeContext(context);
    const entry: AppLogEntry = {
      level: 'error',
      message,
      timestamp: new Date().toISOString(),
      context: normalizedContext,
      ...(typeof window !== 'undefined' && window.location && {
        userAgent: navigator.userAgent,
        url: window.location.href,
      }),
    };
    
    // Extract error information if present
    if (normalizedContext?.error instanceof Error) {
      entry.error = {
        message: normalizedContext.error.message,
        stack: normalizedContext.error.stack,
      };
    } else if (typeof normalizedContext?.error === 'object' && normalizedContext.error !== null) {
      entry.error = {
        message: String(normalizedContext.error),
      };
    } else if (context instanceof Error) {
      entry.error = {
        message: context.message,
        stack: context.stack,
      };
    }
    
    // Always add to app logs (structured, detailed)
    addAppLog(entry);
    
    // Console: Always log errors (basic message)
    console.error(formatConsoleMessage(message, context), normalizedContext || '');
  },
};

/**
 * Log an error with context (structured error logging)
 * Useful for detailed error tracking
 */
export function logError(message: string, error: unknown, context?: Record<string, unknown>): void {
  const errorMessage = error instanceof Error ? error.message : String(error);
  const errorStack = error instanceof Error ? error.stack : undefined;

  const entry: AppLogEntry = {
    level: 'error',
    message,
    timestamp: new Date().toISOString(),
    context: {
      ...context,
      error: errorMessage,
    },
    error: {
      message: errorMessage,
      stack: errorStack,
    },
    ...(typeof window !== 'undefined' && window.location && {
      userAgent: navigator.userAgent,
      url: window.location.href,
    }),
  };
  
  // Always add to app logs (structured, detailed)
  addAppLog(entry);
  
  // Console: Always log errors (basic message)
  console.error(formatConsoleMessage(message, { ...context, error: errorMessage }), {
    error: errorMessage,
    stack: errorStack,
    ...context,
  });
}

/**
 * Get recent app logs (for debugging/monitoring)
 * Returns structured log entries with full context
 */
export function getAppLogs(limit = 50): AppLogEntry[] {
  return appLogs.slice(-limit);
}

/**
 * Clear app logs (useful for testing or memory management)
 */
export function clearAppLogs(): void {
  appLogs.length = 0;
}
